import { Question } from 'types';

const SAMPLE_REPORT_URL = 'https://public-sl-assets.s3.us-east-2.amazonaws.com/report_sample.pdf';
const UPSELL_CALENDAR_URL =
  'https://calendly.com/andrewolsen/searchlight-free-trial-reference-taker';
const UPGRADE_CALENDAR_URL = 'https://calendly.com/andrewolsen/searchlight-free-trial-upgrade';
const FREE_TRIAL_SIGN_UP_MEETING_URL =
  'https://calendly.com/andrewolsen/searchlight-free-trial?back=1&month=2024-02searchlight-onboarding';

const RESEARCH_PAPER_URL =
  'https://www.searchlight.ai/resources/the-value-of-predictive-talent-models';
const FREE_TRIAL_JOB_STEPS = {
  JOB_NAME: 'JOB_NAME',
  JOB_TAXONOMY: 'JOB_TAXONOMY',
  REFERENCE_SURVEY: 'REFERENCE_SURVEY',
  CREATED: 'CREATED',
};
const SAMPLE_MSMC_QUESTION = {
  questionText: 'Of the attributes in this list, what are your top strengths?',
  questionIdentifier: 'question_strengths_multiple_choice_filter_self',
  validations: {
    isConditional: false,
    maxSelections: null,
    required: true,
  },
  questionSurveyInterface: 'filtered_multi_select',
  answerMeta: {
    orderedAnswerOptions: [
      {
        title: 'Communication',
        subtitle: 'Speaks or writes with ease, clarity and impact',
      },
      {
        title: 'Coachability',
        subtitle: 'Listens to, accepts, and applies feedback on doing things better',
      },
      {
        title: 'Attention to Detail',
        subtitle: 'Is rigorously detail-oriented and meticulous',
      },
      {
        title: 'Hardworking',
        subtitle: 'Is disciplined and works vigorously',
      },
      {
        title: 'Creativity',
        subtitle: 'Uses original thinking to generate new ideas and solutions',
      },
      {
        title: 'Dependability',
        subtitle: "Can be relied on and accountable for one's own work",
      },
      {
        title: 'Strategic Thinking',
        subtitle: 'Anticipates and prepares for future events',
      },
      {
        title: 'Collaboration',
        subtitle: 'Works well with others to realize better results',
      },
      {
        title: 'Enthusiasm',
        subtitle: 'Shows a lively and genuine interest in work',
      },
      {
        title: 'Empathy',
        subtitle: 'Identifies with the feelings, ideas and attitudes of others.',
      },
      {
        title: 'Charisma',
        subtitle: 'Generates energy, enthusiasm and a desire to succeed in others',
      },
      {
        title: 'Active Listening',
        subtitle: 'Listens attentively and understands what is being said',
      },
      {
        title: 'Humility',
        subtitle: 'Able to experiment and learn without ego',
      },
      {
        title: 'Critical Thinking',
        subtitle: 'Thinks clearly and rationally to guide beliefs and actions',
      },
      {
        title: 'Adaptability',
        subtitle: 'Adjusts easily to new or changing circumstances',
      },
      {
        title: 'Fast Learner',
        subtitle: 'Masters situations or information easily and quickly',
      },
      {
        title: 'Managing Stress',
        subtitle: 'Performs competently under pressure',
      },
      {
        title: 'Being a Self-Starter',
        subtitle: 'Proactively takes action to achieve and execute results',
      },
      {
        title: 'Personable',
        subtitle: 'Has a friendly, approachable and outgoing style',
      },
      {
        title: 'Curiosity',
        subtitle: 'Driven to learn and stay current professionally',
      },
      {
        title: 'Poise',
        subtitle: 'Calm, collected, and self-controlled under pressure',
      },
      {
        title: 'Ambition',
        subtitle: 'Aspires to and works toward a high level of success',
      },
      {
        title: 'Decisiveness',
        subtitle: 'Acts quickly, confidently and with determination',
      },
      {
        title: 'Conscientiousness',
        subtitle: "Careful and diligent in fulfilling one's tasks and obligations",
      },
      {
        title: 'Teaching Others',
        subtitle: 'Coaches or instructs others to improve their performance',
      },
      {
        title: 'Independence',
        subtitle: 'Functions effectively without assistance or direction from others',
      },
      {
        title: 'Intuition',
        subtitle: 'Successfully uses insight not supported by obvious facts and data',
      },
      {
        title: 'Good Judgement',
        subtitle: 'Forms sound opinions and makes solid decisions',
      },
      {
        title: 'Optimism',
        subtitle: 'Maintains a positive, confident attitude',
      },
      {
        title: 'Persistence',
        subtitle: 'Perseveres in spite of opposition or difficulty',
      },
      {
        title: 'Results-driven',
        subtitle: 'Focuses on achieving results and outcomes',
      },
      {
        title: 'Resourcefulness',
        subtitle: 'Finds alternative solutions to overcome obstacles',
      },
      {
        title: 'Entrepreneurship',
        subtitle:
          'Takes calculated risks and applies unconventional solutions in a business context',
      },
    ],
  },
  answerTopic: 'strengths',
  questionType: 'question_msmc_and_filter',
  questionInterface: 'filtered_multi_select',
} as unknown as Question;
const SAMPLE_CONTINUUM_QUESTION = {
  questionText: 'When I work on something challenging, I will:',
  validations: {
    isConditional: false,
    maxSelections: 0,
    required: true,
  },
  answerTopic: 'culture',
  qohFactorDisplayName: '',
  questionType: 'question_culture_continuum',
  questionTypeKey: '8edc1b82-4c85-40e1-8f97-64f089436a13',
  answerMeta: {
    labelOneTitle: 'Innovative',
    labelOneDescription: 'Adapt quickly to new information and experiment with new ideas',
    labelOneCultureDefinition: {
      name: 'Innovative',
      definition:
        'Willing to experiment, moves fast, quick to take advantage of opportunities, avoids existing procedures, and takes risks',
    },
    labelTwoTitle: 'Structured',
    labelTwoDescription:
      'Create a structured, measured process that aligns with what has worked in the past',
    labelTwoCultureDefinition: {
      name: 'Structured',
      definition:
        'Follows existing procedures, predictable, moves slow to be careful, and is rules-oriented',
    },
  },
  questionSurveyInterface: 'rating_slider',
} as Question;

const SAMPLE_BEHAVIORAL_MODEL = {
  version: 2,
  title: '[IC] Sales Gold Standard',
  cultureTraits: [
    {
      description:
        "Confronts conflict directly, is hard driving, feedback oriented, even if it's at odds with their team",
      inverse: 'Conflict Preventative',
      inverseDescription: 'Avoids conflict and direct feedback',
      traitName: 'Conflict Forward',
      theme: 'Approach to Feedback',
    },
    {
      description: 'Market driven, works to make the customer experience better',
      inverse: 'Self Oriented',
      inverseDescription:
        'Follows intrinsic passions, works on tasks that promote self-improvement and enjoyment',
      traitName: 'Customer Oriented',
      theme: 'Customer Orientation',
    },
    {
      description: 'Focuses on details and tasks to be done, emphasizes quality and being precise',
      inverse: 'Vision Oriented',
      inverseDescription: 'Focuses on the "big picture" impact of the work',
      traitName: 'Detail Oriented',
      theme: 'Operating Altitude',
    },
    {
      description:
        'Has high expectations for performance, believes individuals should be accountable to results and outcomes',
      inverse: 'Supportive',
      inverseDescription:
        'Believes individuals should be provided with what they need, prioritizes security of employment ',
      traitName: 'Results Oriented',
      theme: 'Outcomes Orientation',
    },
    {
      description:
        'Willing to experiment, moves fast, quick to take advantage of opportunities, avoids existing procedures, and takes risks',
      inverse: 'Structured',
      inverseDescription:
        'Follows existing procedures, predictable, moves slow to be careful, and is rules-oriented',
      traitName: 'Innovative',
      theme: 'Risk Propensity',
    },
    {
      description: 'Team oriented, cooperative, invites others to work together',
      inverse: 'Self Reliant',
      inverseDescription: 'Prefers to work alone, individualistic',
      traitName: 'Collaborative',
      theme: 'Team Orientation',
    },
  ],
  winningTraits: [
    {
      name: 'dependability',
      description: "Can be relied on and accountable for one's own work",
      importance: 'superpower',
      densityRatio: 1.878036175710594,
      totalDensity: 33,
      topGroupDensity: 53.48837209302325,
      remainingGroupDensity: 28.48101265822785,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'results_driven',
      description: 'Focuses on achieving results and outcomes',
      importance: 'superpower',
      densityRatio: 1.6497389653535837,
      totalDensity: 35,
      topGroupDensity: 51.162790697674424,
      remainingGroupDensity: 31.0126582278481,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'attention_to_detail',
      description: 'Is rigorously detail-oriented and meticulous',
      importance: 'superpower',
      densityRatio: 1.7963824289405685,
      totalDensity: 33,
      topGroupDensity: 51.162790697674424,
      remainingGroupDensity: 28.48101265822785,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'independence',
      description: 'Functions effectively without assistance or direction from others',
      importance: 'differentiator',
      densityRatio: 2.9089147286821704,
      totalDensity: 21,
      topGroupDensity: 44.18604651162791,
      remainingGroupDensity: 15.18987341772152,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'ambition',
      description: 'Aspires to and works toward a high level of success',
      importance: 'differentiator',
      densityRatio: 2.4025044722719144,
      totalDensity: 21,
      topGroupDensity: 39.53488372093023,
      remainingGroupDensity: 16.455696202531644,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'teaching_others',
      description: 'Coaches or instructs others to improve their performance',
      importance: 'differentiator',
      densityRatio: 2.8264758497316635,
      totalDensity: 11,
      topGroupDensity: 23.25581395348837,
      remainingGroupDensity: 8.227848101265822,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'decisiveness',
      description: 'Acts quickly, confidently and with determination',
      importance: 'differentiator',
      densityRatio: 2.6723044397463003,
      totalDensity: 9,
      topGroupDensity: 18.6046511627907,
      remainingGroupDensity: 6.962025316455696,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'entrepreneurship',
      description:
        'Takes calculated risks and applies unconventional solutions in a business context',
      importance: 'differentiator',
      densityRatio: 3.6744186046511627,
      totalDensity: 5,
      topGroupDensity: 13.953488372093023,
      remainingGroupDensity: 3.79746835443038,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'hardworking',
      description: 'Is disciplined and works vigorously',
      importance: 'baseline',
      densityRatio: 1.4787294384571754,
      totalDensity: 57,
      topGroupDensity: 76.74418604651163,
      remainingGroupDensity: 51.89873417721519,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'coachability',
      description: 'Listens to, accepts, and applies feedback on doing things better',
      importance: 'baseline',
      densityRatio: 1.409366040140172,
      totalDensity: 50,
      topGroupDensity: 65.11627906976744,
      remainingGroupDensity: 46.20253164556962,
      active: true,
      __typename: 'WinningTrait',
    },
    {
      name: 'being_a_self_starter',
      description: 'Proactively takes action to achieve and execute results',
      importance: 'undetermined',
      densityRatio: 1.7536997885835093,
      totalDensity: 32,
      topGroupDensity: 48.837209302325576,
      remainingGroupDensity: 27.848101265822784,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'communication',
      description: 'Speaks or writes with ease, clarity and impact',
      importance: 'undetermined',
      densityRatio: 1.4247745609871856,
      totalDensity: 33,
      topGroupDensity: 44.18604651162791,
      remainingGroupDensity: 31.0126582278481,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'fast_learner',
      description: 'Masters situations or information easily and quickly',
      importance: 'undetermined',
      densityRatio: 2.066860465116279,
      totalDensity: 24,
      topGroupDensity: 41.86046511627907,
      remainingGroupDensity: 20.253164556962027,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'collaboration',
      description: 'Works well with others to realize better results',
      importance: 'undetermined',
      densityRatio: 1.8897009966777412,
      totalDensity: 26,
      topGroupDensity: 41.86046511627907,
      remainingGroupDensity: 22.151898734177212,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'critical_thinking',
      description: 'Thinks clearly and rationally to guide beliefs and actions',
      importance: 'undetermined',
      densityRatio: 1.419661733615222,
      totalDensity: 30,
      topGroupDensity: 39.53488372093023,
      remainingGroupDensity: 27.848101265822784,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'personable',
      description: 'Has a friendly, approachable and outgoing style',
      importance: 'undetermined',
      densityRatio: 0.9186046511627907,
      totalDensity: 39,
      topGroupDensity: 37.2093023255814,
      remainingGroupDensity: 40.50632911392405,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'persistence',
      description: 'Perseveres in spite of opposition or difficulty',
      importance: 'undetermined',
      densityRatio: 1.2248062015503876,
      totalDensity: 31,
      topGroupDensity: 37.2093023255814,
      remainingGroupDensity: 30.37974683544304,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'active_listening',
      description: 'Listens attentively and understands what is being said',
      importance: 'undetermined',
      densityRatio: 1.6701902748414374,
      totalDensity: 23,
      topGroupDensity: 34.883720930232556,
      remainingGroupDensity: 20.88607594936709,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'enthusiasm',
      description: 'Shows a lively and genuine interest in work',
      importance: 'undetermined',
      densityRatio: 1.286046511627907,
      totalDensity: 26,
      topGroupDensity: 32.55813953488372,
      remainingGroupDensity: 25.31645569620253,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'adaptability',
      description: 'Adjusts easily to new or changing circumstances',
      importance: 'undetermined',
      densityRatio: 1.469767441860465,
      totalDensity: 20,
      topGroupDensity: 27.906976744186046,
      remainingGroupDensity: 18.9873417721519,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'strategic_thinking',
      description: 'Anticipates and prepares for future events',
      importance: 'undetermined',
      densityRatio: 1.5545617173524153,
      totalDensity: 18,
      topGroupDensity: 25.581395348837212,
      remainingGroupDensity: 16.455696202531644,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'resourcefulness',
      description: 'Finds alternative solutions to overcome obstacles',
      importance: 'undetermined',
      densityRatio: 2.2454780361757107,
      totalDensity: 14,
      topGroupDensity: 25.581395348837212,
      remainingGroupDensity: 11.39240506329114,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'humility',
      description: 'Able to experiment and learn without ego',
      importance: 'undetermined',
      densityRatio: 1.5975733063700708,
      totalDensity: 16,
      topGroupDensity: 23.25581395348837,
      remainingGroupDensity: 14.556962025316455,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'charisma',
      description: 'Generates energy, enthusiasm and a desire to succeed in others',
      importance: 'undetermined',
      densityRatio: 1.3608957795004306,
      totalDensity: 18,
      topGroupDensity: 23.25581395348837,
      remainingGroupDensity: 17.088607594936708,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'creativity',
      description: 'Uses original thinking to generate new ideas and solutions',
      importance: 'undetermined',
      densityRatio: 1.8372093023255813,
      totalDensity: 14,
      topGroupDensity: 23.25581395348837,
      remainingGroupDensity: 12.658227848101266,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'empathy',
      description: 'Identifies with the feelings, ideas and attitudes of others.',
      importance: 'undetermined',
      densityRatio: 1.3122923588039868,
      totalDensity: 18,
      topGroupDensity: 23.25581395348837,
      remainingGroupDensity: 17.72151898734177,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'conscientiousness',
      description: "Careful and diligent in fulfilling one's tasks and obligations",
      importance: 'undetermined',
      densityRatio: 1.3997785160575862,
      totalDensity: 14,
      topGroupDensity: 18.6046511627907,
      remainingGroupDensity: 13.291139240506327,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'curiosity',
      description: 'Driven to learn and stay current professionally',
      importance: 'undetermined',
      densityRatio: 1.547123623011016,
      totalDensity: 13,
      topGroupDensity: 18.6046511627907,
      remainingGroupDensity: 12.025316455696203,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'optimism',
      description: 'Maintains a positive, confident attitude',
      importance: 'undetermined',
      densityRatio: 1.0288372093023255,
      totalDensity: 15,
      topGroupDensity: 16.27906976744186,
      remainingGroupDensity: 15.822784810126583,
      active: false,
      __typename: 'WinningTrait',
    },
    {
      name: 'managing_stress',
      description: 'Performs competently under pressure',
      importance: 'undetermined',
      densityRatio: 1.071705426356589,
      totalDensity: 15,
      topGroupDensity: 16.27906976744186,
      remainingGroupDensity: 15.18987341772152,
      active: false,
      __typename: 'WinningTrait',
    },
  ],
  winningWorkStyles: [
    {
      name: 'Innovative',
      description:
        'Willing to experiment, moves fast, quick to take advantage of opportunities, avoids existing procedures, and takes risks',
      topGroupScore: 60,
      remainingGroupScore: 58,
      selectedScore: 60,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Conflict Forward',
      description:
        'Confronts conflict directly, is hard driving, feedback oriented, even if it’s at odds with their team',
      topGroupScore: 77,
      remainingGroupScore: 67,
      selectedScore: 77,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Supportive',
      description:
        'Believes individuals should be provided with what they need, prioritizes security of employment',
      topGroupScore: 23,
      remainingGroupScore: 33,
      selectedScore: 23,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Independent',
      description:
        'Focus on contributing maximally on individual work goals to help the team succeed',
      topGroupScore: 42,
      remainingGroupScore: 39,
      selectedScore: 42,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Collaborative',
      description:
        'Look for ways to lend expertise to teammates and cultivate opportunities to share learnings',
      topGroupScore: 58,
      remainingGroupScore: 61,
      selectedScore: 58,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Detail Oriented',
      description: 'Focuses on details and tasks to be done, emphasizes quality and being precise',
      topGroupScore: 53,
      remainingGroupScore: 62,
      selectedScore: 53,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Results Oriented',
      description:
        'Has high expectations for performance, believes individuals should be accountable to results and outcomes',
      topGroupScore: 77,
      remainingGroupScore: 67,
      selectedScore: 77,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Vision Oriented',
      description: 'Focuses on the “big picture” impact of the work',
      topGroupScore: 47,
      remainingGroupScore: 38,
      selectedScore: 47,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Customer Oriented',
      description:
        'Invest in programs and resources that support team members’ psychological well-being and long-term professional growth',
      topGroupScore: 78,
      remainingGroupScore: 63,
      selectedScore: 78,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Structured',
      description:
        'Follows existing procedures, predictable, moves slow to be careful, and is rules-oriented',
      topGroupScore: 40,
      remainingGroupScore: 42,
      selectedScore: 40,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Conflict Avoidant',
      description: 'Avoids conflict and direct feedback',
      topGroupScore: 23,
      remainingGroupScore: 33,
      selectedScore: 23,
      __typename: 'WinningWorkStyle',
    },
    {
      name: 'Self Oriented',
      description:
        'Invest in tools and resources that enhance the team’s ability to achieve business goals',
      topGroupScore: 22,
      remainingGroupScore: 37,
      selectedScore: 22,
      __typename: 'WinningWorkStyle',
    },
  ],
};

const SIGN_UP_MEETING_FLAG = 'show-sign-up-meeting';

export {
  SAMPLE_REPORT_URL,
  UPSELL_CALENDAR_URL,
  UPGRADE_CALENDAR_URL,
  FREE_TRIAL_SIGN_UP_MEETING_URL,
  FREE_TRIAL_JOB_STEPS,
  SAMPLE_MSMC_QUESTION,
  SAMPLE_CONTINUUM_QUESTION,
  RESEARCH_PAPER_URL,
  SAMPLE_BEHAVIORAL_MODEL,
  SIGN_UP_MEETING_FLAG,
};
